/**
 * The months CoLiviq has actually transferred to the signed-in owner
 * (COL-528), newest first.
 *
 * Every figure comes from `owner_payout_statements` (20261012085008) already
 * assembled — nothing here recomputes a fee or a net. That is the whole point
 * of the RPC: the admin side derives these numbers from several places, and a
 * second derivation in the owner's browser is how the two end up disagreeing
 * about how much money somebody received.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { PayoutFeeLine } from "@/lib/owner-payout-fee";

export type OwnerPayoutStatement = {
  invoice_id: string;
  invoice_no: string | null;
  /** First day of the month covered, YYYY-MM-DD. */
  period_month: string;
  property_id: number | null;
  property_title: string | null;
  rent_collected_cents: number;
  fee_cents: number;
  net_payout_cents: number;
  /** When the transfer was recorded. */
  paid_at: string | null;
  /**
   * The invoice's fee_basis, one entry per billed unit — what the fee was
   * charged on (COL-609). Describe it through explainFee(); empty for an
   * invoice with no per-unit snapshot.
   */
  fee_lines: PayoutFeeLine[] | null;
  /** The invoice's lessor: who the statement is addressed to. */
  owner_name: string | null;
  owner_address: string | null;
  /** Last four characters of the owner's payout IBAN, masked server-side. */
  owner_iban_last4: string | null;
};

/**
 * Keyed by account, as accountBillingAddressKey already is. Neither sign-out
 * nor impersonation clears the QueryClient, so an unscoped key hands the next
 * account the previous owner's rows while its own request is in flight — and
 * impersonation is exactly how this portal is meant to be reviewed first.
 */
export function ownerPayoutStatementsKey(userId: string | undefined) {
  return ["owner", "payout-statements", userId] as const;
}

function isMissingFunction(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "PGRST202" || error?.code === "42883") return true;
  return /could not find the function|does not exist/i.test(
    error?.message ?? ""
  );
}

export function useOwnerPayoutStatements(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ownerPayoutStatementsKey(user?.id),
    enabled: enabled && !!user?.id,
    queryFn: async (): Promise<OwnerPayoutStatement[]> => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{
            data: unknown;
            error: { code?: string; message?: string } | null;
          }>;
        }
      ).rpc("owner_payout_statements");
      if (error) {
        // The dev project trails LIVE; an empty list is also what a brand-new
        // owner sees, so degrade rather than showing an error they cannot act on.
        if (isMissingFunction(error)) return [];
        throw error;
      }
      return (data ?? []) as OwnerPayoutStatement[];
    },
  });
}
